import { useTranslation } from 'react-i18next'
interface Props {
  open: boolean
  onClose: () => void
  onSelectMode: (mode: 'simple' | 'advanced' | 'merge' | 'multiMerge') => void
}

export default function ModeSelectModal({ open, onClose, onSelectMode }: Props) {
  const { t } = useTranslation()
  if (!open) return null
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[560px] overflow-hidden"
        onClick={e => e.stopPropagation()}>

        <div className="px-5 py-4 flex items-center justify-between" style={{ background: '#1a2942' }}>
          <div>
            <h2 className="text-[15px] font-bold text-white">{t('register.mode.title')}</h2>
            <p className="text-[11px] text-slate-400 mt-0.5">{t('register.mode.subtitle')}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition p-1 text-lg leading-none">✕</button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <button
            onClick={() => onSelectMode('simple')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-green-500 hover:bg-green-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">{t('register.mode.simple.title')}</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">{t('register.mode.simple.desc')}</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {(['file', 'title', 'crs', 'recommended'] as const).map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t(`register.mode.simple.tags.${tag}`)}</span>
              ))}
            </div>
          </button>

          <button
            onClick={() => onSelectMode('advanced')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-blue-500 hover:bg-blue-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">{t('register.mode.advanced.title')}</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">{t('register.mode.advanced.desc')}</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {(['lod', 'proj4', 'upAxis', 'curvature', 'offset'] as const).map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t(`register.mode.advanced.tags.${tag}`)}</span>
              ))}
            </div>
            <span className="text-[10px] text-blue-600 font-bold">{t('register.mode.advanced.expert')}</span>
          </button>

          <button
            onClick={() => onSelectMode('merge')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-slate-500 hover:bg-slate-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v12a2 2 0 002 2h10M18 15l-4-4 4-4"/><path d="M8 5H4a2 2 0 00-2 2v10a2 2 0 002 2h4"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">{t('register.mode.mergeExisting.title')}</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">{t('register.mode.mergeExisting.desc')}</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {(['select', 'noReconvert'] as const).map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t(`register.mode.mergeExisting.tags.${tag}`)}</span>
              ))}
            </div>
          </button>

          <button
            onClick={() => onSelectMode('multiMerge')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-slate-500 hover:bg-slate-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">{t('register.mode.mergeNew.title')}</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">{t('register.mode.mergeNew.desc')}</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {(['upload', 'sequential', 'merge'] as const).map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t(`register.mode.mergeNew.tags.${tag}`)}</span>
              ))}
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}
